import { useCallback, useEffect, useRef, useState } from "react";
import { ClockTypes } from "@repo/database";
import { intlFormat } from "date-fns";
import { toZonedTime } from "date-fns-tz";

import { useUser } from "~/hooks/use-user";

export const Clock = () => {
  const { user } = useUser();
  const [time, setTime] = useState(toZonedTime(new Date(), user.timeZone));
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  // Function to update time and check if it needs to be updated
  const updateTime = useCallback(() => {
    const newTime = toZonedTime(new Date(), user.timeZone);
    setTime((prevTime) => {
      if (
        prevTime.getMinutes() !== newTime.getMinutes() ||
        prevTime.getHours() !== newTime.getHours()
      ) {
        return newTime;
      }
      return prevTime;
    });
  }, [user.timeZone]);

  // Function to synchronize with minute boundaries
  const synchronizeWithMinuteBoundary = useCallback(() => {
    // Clear any existing timers
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    // Update time immediately
    updateTime();

    // Calculate milliseconds until next minute
    const now = toZonedTime(new Date(), user.timeZone);
    const millisToNextMinute =
      (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    // Set timeout to align with the next minute boundary
    timeoutRef.current = setTimeout(() => {
      // Update time at the minute boundary
      updateTime();

      // Set interval for subsequent updates every minute
      intervalRef.current = setInterval(() => {
        updateTime();

        // Re-synchronize every hour to prevent drift
        const currentDate = toZonedTime(new Date(), user.timeZone);
        if (currentDate.getMinutes() === 0) {
          synchronizeWithMinuteBoundary();
        }
      }, 60000);
    }, millisToNextMinute);
  }, [updateTime, user.timeZone]);

  useEffect(() => {
    // Initial synchronization
    synchronizeWithMinuteBoundary();

    // Handle visibility changes (tab focus, device sleep/wake)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        synchronizeWithMinuteBoundary();
      }
    };

    // Handle window focus
    const handleFocus = () => {
      synchronizeWithMinuteBoundary();
    };

    // Handle time zone changes or system time adjustments
    const handleTimeChanges = () => {
      const minuteCheck = setInterval(() => {
        const systemTime = new Date();
        const displayedTime = time;

        // If difference is more than 1 minute, resynchronize
        if (Math.abs(systemTime - displayedTime) > 60000) {
          synchronizeWithMinuteBoundary();
        }
      }, 30000); // Check every 30 seconds

      return () => clearInterval(minuteCheck);
    };

    const timeChangeCleanup = handleTimeChanges();

    // Add event listeners
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    // Cleanup function
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.addEventListener("focus", handleFocus);
      timeChangeCleanup();
    };
  }, [synchronizeWithMinuteBoundary, time]);

  return (
    <span className="font-bold">
      {intlFormat(
        time,
        {
          hour: "2-digit",
          minute: "2-digit",
          hourCycle: user.clock === ClockTypes.military ? "h23" : "h11",
        },
        { locale: user.language },
      )}
    </span>
  );
};
